import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

// Month date picker for the "Add planned block" popover's Day field — same
// look as the Timer modal's date picker (timer-tracker__date-picker), and
// as the one inside <planner-log-timerow>. The parent decides when it's
// shown and where; this only renders the month and reports the picked day.
@Component({
    selector: 'planner-date-picker',
    templateUrl: './planner-date-picker.component.html',
    styleUrls: ['./planner-date-picker.component.scss'],
})
export class PlannerDatePickerComponent implements OnChanges {
    @Input() selected: Date;
    // Earliest day that can be picked (any time of day — compared by date);
    // earlier days show greyed out. None by default, as in the Planner.
    @Input() minDate: Date | null = null;
    @Output() picked = new EventEmitter<Date>();

    pickerMonth = new Date();

    private get minDay(): Date | null {
        if (!this.minDate) return null;
        const d = new Date(this.minDate);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    isBeforeMin(date: Date | null): boolean {
        const min = this.minDay;
        return !!date && !!min && date < min;
    }

    /** No going back to months that are entirely before minDate. */
    get canGoPrev(): boolean {
        const min = this.minDay;
        return !min || this.pickerMonth > new Date(min.getFullYear(), min.getMonth(), 1);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selected'] && this.selected) {
            this.pickerMonth = new Date(this.selected.getFullYear(), this.selected.getMonth(), 1);
        }
    }

    prevMonth(): void {
        if (!this.canGoPrev) return;
        this.pickerMonth = new Date(this.pickerMonth.getFullYear(), this.pickerMonth.getMonth() - 1, 1);
    }

    nextMonth(): void {
        this.pickerMonth = new Date(this.pickerMonth.getFullYear(), this.pickerMonth.getMonth() + 1, 1);
    }

    get monthLabel(): string {
        return this.pickerMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }

    /** Mon-first 7-column grid of Date | null entries. */
    get days(): (Date | null)[] {
        const year = this.pickerMonth.getFullYear();
        const month = this.pickerMonth.getMonth();
        const lastDay = new Date(year, month + 1, 0);
        let startDow = new Date(year, month, 1).getDay();
        startDow = startDow === 0 ? 6 : startDow - 1;
        const days: (Date | null)[] = [];
        for (let i = 0; i < startDow; i++) days.push(null);
        for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(year, month, d));
        while (days.length % 7 !== 0) days.push(null);
        return days;
    }

    isToday(date: Date | null): boolean {
        return !!date && date.toDateString() === new Date().toDateString();
    }

    isSelected(date: Date | null): boolean {
        return !!date && !!this.selected && date.toDateString() === this.selected.toDateString();
    }

    onDayClick(day: Date | null): void {
        if (day && !this.isBeforeMin(day)) this.picked.emit(new Date(day));
    }

    trackByIndex(index: number): number { return index; }
}
